import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { acessibilidadeEn } from '../../web/src/i18n/acessibilidade-en';
import { acessibilidadeEs } from '../../web/src/i18n/acessibilidade-es';
import { acessibilidadePt } from '../../web/src/i18n/acessibilidade-pt';
import { discretoEn } from '../../web/src/i18n/discreto-en';
import { discretoEs } from '../../web/src/i18n/discreto-es';
import { discretoPt } from '../../web/src/i18n/discreto-pt';
import { en } from '../../web/src/i18n/en';
import { es } from '../../web/src/i18n/es';
import { pt } from '../../web/src/i18n/pt';
import { ajustarPlural, type ResolvedorPlural } from '../../web/src/i18n/plural';
import { definirIdiomaFormato, type Idioma } from '../../web/src/nucleo/formato';
import { portalEn } from './i18n/en';
import { portalEs } from './i18n/es';
import { portalPt } from './i18n/pt';

/**
 * Portal do Cliente (Onda 1): o i18n do núcleo do MalgoOS (namespace `nucleo`: estados, erros por código, componentes) mais o
 * namespace `portal`. Os códigos de erro do portal entram no dicionário de erros do núcleo, para o `mensagemDeErro` do
 * MalgoOS traduzir em en e es como no sistema da equipe. Idioma salvo no aparelho (`malgo.portal.idioma`) ou o do navegador.
 */
const nucleo = (base: Record<string, unknown>, ...extras: Record<string, unknown>[]) => Object.assign({}, base, ...extras);
const comErros = (b: Record<string, unknown>, codigos: Record<string, string>) => {
  const erros = (b.erros ?? {}) as { codigos?: Record<string, string> };
  return { ...b, erros: { ...erros, codigos: { ...erros.codigos, ...codigos } } };
};

// U15 F3: o erro de carga do cliente manda falar com a equipe que o atende, não com o "administrador do sistema".
const comContatoNoErro = (b: Record<string, unknown>, frase: string) => ({ ...b, estados: { ...(b.estados as Record<string, unknown>), erroContinua: frase } });

const recursos = {
  pt: { nucleo: comContatoNoErro(comErros(nucleo(pt, acessibilidadePt, discretoPt), portalPt.erros.codigos), 'Se continuar, avise a equipe que atende você.'), portal: portalPt },
  en: { nucleo: comContatoNoErro(comErros(nucleo(en, acessibilidadeEn, discretoEn), portalEn.erros.codigos), 'If it keeps happening, tell the team that serves you.'), portal: portalEn },
  es: { nucleo: comContatoNoErro(comErros(nucleo(es, acessibilidadeEs, discretoEs), portalEs.erros.codigos), 'Si continúa, avisa al equipo que te atiende.'), portal: portalEs },
};

function idiomaInicial(): Idioma {
  try {
    const salvo = localStorage.getItem('malgo.portal.idioma');
    if (salvo === 'pt' || salvo === 'en' || salvo === 'es') return salvo;
  } catch {
    /* sem storage */
  }
  const nav = (typeof navigator !== 'undefined' ? navigator.language : 'pt').slice(0, 2);
  return nav === 'en' || nav === 'es' ? nav : 'pt';
}

const langHtml = (i: Idioma) => (i === 'pt' ? 'pt-BR' : i);
const inicial = idiomaInicial();
definirIdiomaFormato(inicial);
if (typeof document !== 'undefined') document.documentElement.lang = langHtml(inicial);

void i18n.use(initReactI18next).init({
  resources: recursos,
  lng: inicial,
  fallbackLng: 'pt',
  defaultNS: 'nucleo',
  ns: ['nucleo', 'portal'],
  interpolation: { escapeValue: false },
  returnNull: false,
});
ajustarPlural(i18n.services.pluralResolver as ResolvedorPlural);

export function trocarIdioma(i: Idioma) {
  definirIdiomaFormato(i);
  try {
    localStorage.setItem('malgo.portal.idioma', i);
  } catch {
    /* sem storage */
  }
  document.documentElement.lang = langHtml(i);
  return i18n.changeLanguage(i);
}

export { recursos };
export default i18n;
